import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
// import { convertCurrency } from "../helper/CurrencyConverter";

const getCurrencySymbol = (currency) => {
  switch (currency) {
    case "INR":
      return "₹";

    case "USD":
      return "$";

    case "EUR":
      return "€";

    case "AED":
      return "AED ";

    case "SAR":
      return "SAR ";

    case "QAR":
      return "QAR ";

    case "GBP":
      return "£";

    default:
      return "$";
  }
};

const API_URL = process.env.REACT_APP_API_URL;

/* ─── Max swatches shown before "+N more" ── */
const MAX_SWATCHES = 5;

/* ─── Single Product Card ─────────────────────────────────── */
const ProductCard = ({ item, selectedCurrency }) => {
  const variants = item.variants || [];

  /* Find all colour variants — we treat each variant that has a `color`
     field (or `colorName` / `colorCode`) as a swatch entry.             */
  const colorVariants = variants.filter(
    (v) => v.color || v.colorName || v.colorCode || v.colorHex,
  );

  const hasColors = colorVariants.length > 0;

  /* Active variant index — -1 means "base product" */
  const [activeIdx, setActiveIdx] = useState(-1);

  /* Derive display values from active variant or base product */
  const activeVariant = activeIdx >= 0 ? colorVariants[activeIdx] : null;

  const displayImage =
    activeVariant?.images?.[0] ||
    activeVariant?.image ||
    item.images?.[0] ||
    "assets/images/thumbs/product-img1.png";

  const variantPrice =
  activeVariant?.price ||
  item?.variants?.[0]?.price ||
  item?.price ||
  {};

const displayPrice =
  typeof variantPrice === "object"
    ? (
        variantPrice?.[
          selectedCurrency
        ] ||
        variantPrice?.INR
      )
    : variantPrice || 0;

  const variantOldPrice =
  activeVariant?.oldPrice ||
  item?.variants?.[0]?.oldPrice ||
  item?.oldPrice ||
  null;

const displayOldPrice =
  typeof variantOldPrice === "object"
    ? (
        variantOldPrice?.[
          selectedCurrency
        ] ||
        variantOldPrice?.INR
      )
    : variantOldPrice;

  /* Reviews */
  const reviews = item.reviews || [];
  const total = reviews.length;
  const avg =
    total > 0
      ? (reviews.reduce((s, r) => s + (r.ratings || 0), 0) / total).toFixed(1)
      : "0.0";

  /* Swatch colour value — prefer colorHex, else colorCode, else colorName */
  const swatchColor = (v) =>
    v.colorHex || v.colorCode || v.color || v.colorName || "#ccc";

  const visibleSwatches = colorVariants.slice(0, MAX_SWATCHES);
  const extraCount = colorVariants.length - MAX_SWATCHES;

  return (
    <div className="pl-card">
      {/* ── Image ── */}
      <Link to={`/product-details/${item._id}`} className="pl-card__thumb">
        <img src={displayImage} alt={item.name} className="pl-card__img" />
      </Link>

      {/* ── Colour swatches ── */}
      {hasColors && (
        <div className="pl-swatches">
          {visibleSwatches.map((v, i) => (
            <button
              key={i}
              className={`pl-swatch ${activeIdx === i ? "pl-swatch--active" : ""}`}
              style={{ background: swatchColor(v) }}
              title={v.colorName || v.color || `Color ${i + 1}`}
              onClick={() => setActiveIdx(activeIdx === i ? -1 : i)}
              aria-label={`Select color ${v.colorName || i + 1}`}
            />
          ))}
          {extraCount > 0 && (
            <Link
              to={`/product-details/${item._id}`}
              className="pl-swatch-more"
            >
              +{extraCount}
            </Link>
          )}
        </div>
      )}

      {/* ── Content ── */}
      <div className="pl-card__content">
        {/* Name */}
        <h6 className="pl-card__name">
          <Link to={`/product-details/${item._id}`} title={item.name}>
            {item.name}
          </Link>
        </h6>

        {/* Rating */}
        <div className="pl-card__rating">
          <span className="pl-rating__avg">{avg}</span>
          <span className="pl-rating__star">
            <i className="ph-fill ph-star" />
          </span>
          <span className="pl-rating__count">({total})</span>
        </div>

        {/* Price */}
        <div className="pl-card__price">
          <span className="pl-price--now">
            <div className="pl-card__price">
              {displayOldPrice && (
                <span className="pl-price--old">
                  {getCurrencySymbol(selectedCurrency)}

                  {Number(displayOldPrice).toFixed(2)}
                </span>
              )}

              <span className="pl-price--now">
                {getCurrencySymbol(selectedCurrency)}

                {Number(displayPrice).toFixed(2)}
              </span>
            </div>
          </span>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────────── */
const ProductListOne = ({ selectedCurrency }) => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_URL}/product/all`);
        console.log(res.data.data);
        if (res.data.success) {
          const active = res.data.data.filter((p) => p.status === "Active");
          setProducts(active.slice(0, 30));
        }
      } catch (err) {
        console.error("Error fetching products:", err);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="pl-root mt-24">
      <div className="container container-lg">
        <div className="pl-grid">
          {products.map((item) => (
            <ProductCard
              key={item._id}
              item={item}
              selectedCurrency={selectedCurrency}
            />
          ))}
        </div>
      </div>

      <style>{`
        /* ══════════════════════════════════════
           CSS Variables
           ══════════════════════════════════════ */
        .pl-root {
          --pl-primary:    var(--main-600, #1a8a3c);
          --pl-border:     #e3e6e8;
          --pl-radius:     12px;
          --pl-text:       #111;
          --pl-sub:        #565959;
          --pl-price:      #b12704;
        }

        /* ── Grid ── */
        .pl-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 14px;
        }

        /* ── Card ── */
        .pl-card {
          background: #fff;
          border: 1px solid var(--pl-border);
          border-radius: var(--pl-radius);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transition: box-shadow .2s, border-color .2s;
        }
        .pl-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.11);
          border-color: #adb1b8;
        }

        /* ── Thumb ── */
        .pl-card__thumb {
          display: block;
          width: 100%;
          height: 220px;
          overflow: hidden;
          background: #f9fafb;
        }
        .pl-card__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform .28s;
        }
        .pl-card:hover .pl-card__img { transform: scale(1.05); }

        /* ══════════════════════════════════════
           COLOUR SWATCHES  (Amazon style)
           ══════════════════════════════════════ */
        .pl-swatches {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 10px 4px;
          flex-wrap: wrap;
        }

        /* Each circular swatch button */
        .pl-swatch {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid transparent;
          outline: 2px solid transparent;
          cursor: pointer;
          padding: 0;
          transition: outline .15s, border .15s, transform .15s;
          flex-shrink: 0;
        }
        .pl-swatch:hover {
          transform: scale(1.18);
          outline: 2px solid #aaa;
          outline-offset: 1px;
        }
        /* Active swatch — white ring + green outer ring */
        .pl-swatch--active {
          border: 2px solid #fff;
          outline: 2px solid var(--pl-primary);
          outline-offset: 1px;
          transform: scale(1.12);
        }

        /* "+N more" link */
        .pl-swatch-more {
          font-size: 11.5px;
          color: var(--pl-primary);
          font-weight: 600;
          text-decoration: none;
          white-space: nowrap;
          margin-left: 2px;
        }
        .pl-swatch-more:hover { text-decoration: underline; }

        /* ── Content ── */
        .pl-card__content {
          padding: 4px 10px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        /* Name */
        .pl-card__name {
          margin: 0;
          font-size: 13.5px;
          font-weight: 600;
          line-height: 1.35;
        }
        .pl-card__name a {
          color: var(--pl-text);
          text-decoration: none;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .pl-card__name a:hover { color: var(--pl-primary); text-decoration: underline; }

        /* Rating */
        .pl-card__rating {
          display: flex;
          align-items: center;
          gap: 3px;
        }
        .pl-rating__avg  { font-size: 12px; font-weight: 700; color: #555; }
        .pl-rating__star { font-size: 13px; color: #f0a500; display: flex; }
        .pl-rating__count{ font-size: 11.5px; color: var(--pl-sub); }

        /* Price */
        .pl-card__price {
          display: flex;
          align-items: baseline;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 2px;
        }
        .pl-price--now {
          font-size: 15px;
          font-weight: 700;
          color: var(--pl-price);
        }
        .pl-price--old {
          font-size: 12px;
          color: #888;
          text-decoration: line-through;
        }

        /* ══════════════════════════════════════
           RESPONSIVE
           ══════════════════════════════════════ */
        @media (max-width: 1399px) {
          .pl-grid { grid-template-columns: repeat(5, 1fr); }
        }
        @media (max-width: 1199px) {
          .pl-grid { grid-template-columns: repeat(4, 1fr); }
        }
        @media (max-width: 991px) {
          .pl-grid { grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .pl-card__thumb { height: 170px; }
          .pl-card__name { font-size: 12.5px; }
          .pl-price--now { font-size: 13.5px; }

          /* hide rating & old price on tablet to save space */
          .pl-card__rating { display: none; }
          .pl-price--old   { display: none; }
        }
        @media (max-width: 575px) {
          .pl-grid { grid-template-columns: repeat(2, 1fr); gap: 6px; }
          .pl-card__thumb { height: 130px; }
          .pl-card__content { padding: 4px 7px 10px; }
          .pl-card__name { font-size: 11.8px; }
          .pl-price--now { font-size: 12.5px; }

          /* smaller swatches on phone */
          .pl-swatch { width: 16px; height: 16px; }
          .pl-swatches { gap: 4px; padding: 6px 7px 2px; }
          .pl-swatch-more { font-size: 10.5px; }
        }
      `}</style>
    </div>
  );
};

export default ProductListOne;
